import { useState } from "react";
import FeedbackCategoryBadge from "@component/feedback-category-badge";
import FeedbackPointsList from "@container/feedback-points-list";
import { FeedbackItem } from "@type/interview";
import { MessageSquareIcon } from "lucide-react";
import { cn } from "@util/tailwind";

type Category = "Brilliant" | "Excellent" | "Good" | "Inaccuracy" | "Mistake";
type FilterCategory = Category | "All";

const CATEGORY_ORDER: Category[] = [
    "Brilliant",
    "Excellent",
    "Good",
    "Inaccuracy",
    "Mistake",
];

interface Props {
    feedback: FeedbackItem[];
    language?: string;
}

export default function FeedbackOverview({ feedback, language }: Props) {
    // Count per-category once, in declaration order, so we can iterate.
    const feedbackCounts: Record<Category, number> = {
        Brilliant: 0,
        Excellent: 0,
        Good: 0,
        Inaccuracy: 0,
        Mistake: 0,
    };
    for (const item of feedback) {
        if (item.category in feedbackCounts) {
            feedbackCounts[item.category as Category]++;
        }
    }
    const totalCount = CATEGORY_ORDER.reduce((sum, c) => sum + feedbackCounts[c], 0);

    // Default to "All" so the candidate sees every feedback item up-front.
    const [selectedCategory, setSelectedCategory] = useState<FilterCategory>("All");

    const filteredFeedbackPoints =
        selectedCategory === "All"
            ? CATEGORY_ORDER.flatMap((c) =>
                  feedback.filter((item) => item.category === c)
              )
            : feedback.filter((item) => item.category === selectedCategory);

    if (totalCount === 0) {
        return (
            <div className="rounded-md">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                    <MessageSquareIcon className="w-4 h-4 text-blue-600" />
                    <h3 className="text-sm font-semibold text-gray-900">Review feedback</h3>
                </div>
                <div className="p-10 text-center">
                    <div className="bg-gray-100 p-3 rounded-full inline-block mb-3">
                        <MessageSquareIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">
                        No feedback available yet.
                    </h4>
                    <p className="text-xs text-gray-500 max-w-sm mx-auto">
                        Once your interview has been analyzed, feedback will appear here organized by category.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-md">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <MessageSquareIcon className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-gray-900">Review feedback</h3>
                <span className="inline-flex items-center justify-center min-w-[24px] h-[22px] px-2 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold tabular-nums">
                    {totalCount}
                </span>
            </div>

            {/* Category filter */}
            <div className="px-5 py-3 border-b border-gray-100">
                <p className="text-[11px] uppercase tracking-wider text-neutral-500 font-medium mb-2">
                    Filter by category
                </p>
                <div className="flex flex-wrap gap-2">
                    {/* "All" filter — shows every feedback item across categories */}
                    <button
                        type="button"
                        onClick={() => setSelectedCategory("All")}
                        className="focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 rounded-full"
                        aria-pressed={selectedCategory === "All"}
                    >
                        <span
                            className={cn(
                                "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                                selectedCategory === "All"
                                    ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                    : "bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100"
                            )}
                        >
                            All
                            {totalCount > 0 && (
                                <span
                                    className={cn(
                                        "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold tabular-nums",
                                        selectedCategory === "All"
                                            ? "bg-blue-800/40 text-white"
                                            : "bg-blue-100 text-blue-800"
                                    )}
                                >
                                    {totalCount}
                                </span>
                            )}
                        </span>
                    </button>

                    {CATEGORY_ORDER.map((category) => (
                        <button
                            key={category}
                            type="button"
                            onClick={() => setSelectedCategory(category)}
                            className="focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 rounded-full"
                            aria-pressed={selectedCategory === category}
                        >
                            <FeedbackCategoryBadge
                                category={category}
                                count={feedbackCounts[category]}
                                language={language}
                                selected={selectedCategory === category}
                            />
                        </button>
                    ))}
                </div>
            </div>

            {/* Feedback content */}
            <div className="px-5 py-4 max-h-[600px] overflow-y-auto">
                <FeedbackPointsList points={filteredFeedbackPoints} />
            </div>
        </div>
    );
}
